import React, { FC } from 'react'
import {
  Grid,
  Box,
  Text,
  Button,
  Center,
  AlertTag,
  AlertTagVariants,
} from '@lojinha/design-system'
import styled from 'styled-components'
import { testId } from '@lojinha/helpers'
import { PurchasesVariants } from '../../core/purchase-variants'
import { t } from '../../dictionary'
import { SectionBannerVariants } from '../../core/section-banner-variants'
import { bannerStyles } from './variants'
import { beholder } from '@lojinha/beholder'

const BannerContainer = styled(Box)<{
  variant: PurchasesVariants | SectionBannerVariants
  hasPaddingBottom: boolean
}>(
  ({ theme, variant, hasPaddingBottom }) => `
    background-color: ${bannerStyles[variant].backgroundColor};
    padding-bottom: ${hasPaddingBottom ? theme.space.x128 : ''};

    ${theme.media.below('sm')} {
      padding-bottom: ${hasPaddingBottom ? theme.space.x64 : ''};
    }
  `
)

const Title = styled(Text)(
  ({ theme }) => `
    margin-bottom: ${theme.space.x16};
    ${theme.media.below('md')}{
      margin-bottom: ${theme.space.x8};
    }
  `
)

const variantHasTitle = (
  variant: PurchasesVariants | SectionBannerVariants
): boolean => {
  return [
    PurchasesVariants.MyPurchases,
    SectionBannerVariants.EditProfile,
    SectionBannerVariants.SurveyActive,
    SectionBannerVariants.SurveyAnswered,
    SectionBannerVariants.SurveyExpired,
    SectionBannerVariants.SurveyAlreadyFilled,
    SectionBannerVariants.SurveyLoading,
    SectionBannerVariants.SomethingWentWrong,
  ].includes(variant)
}

const variantHasPaddingBottom = (
  variant: PurchasesVariants | SectionBannerVariants
): boolean => {
  return ([
    SectionBannerVariants.EditProfile,
    SectionBannerVariants.SurveyActive,
    SectionBannerVariants.SurveyAnswered,
  ] as Array<PurchasesVariants | SectionBannerVariants>).includes(variant)
}

type SectionBannerProps = {
  bannerVariant?: PurchasesVariants | SectionBannerVariants
  orderId?: number
  deliveryDate?: string
  paymentDue?: string
  orderDate?: string
  trackingUrl?: string
  onClick?: () => void
  bannerActionFeedback?: {
    isLoading?: boolean
    errorMessage?: string
    successMessage?: string
  }
}

export const SectionBanner: FC<SectionBannerProps> = ({
  bannerVariant = PurchasesVariants.MyPurchases,
  orderId = '',
  deliveryDate = '',
  paymentDue = '',
  orderDate = '',
  trackingUrl = '',
  onClick,
  bannerActionFeedback = {
    isLoading: false,
  },
}) => {
  const shouldKeepTitle = variantHasTitle(bannerVariant)
  const hasPaddingBottom = variantHasPaddingBottom(bannerVariant)

  const { isLoading, successMessage, errorMessage } = bannerActionFeedback

  function renderErrorMessage(errorMessage: string) {
    return <AlertTag content={errorMessage} variant={AlertTagVariants.error} />
  }

  function renderBannerAction() {
    if (!bannerStyles[bannerVariant].buttonTitle || !onClick) {
      return
    }

    return (
      <Button
        ariaLabel={
          bannerStyles[bannerVariant].buttonAriaLabel ||
          bannerStyles[bannerVariant].buttonTitle
        }
        onClick={onClick}
        size="small"
        fontColor={bannerStyles[bannerVariant].fontColorButton}
        color={bannerStyles[bannerVariant].backgroundButton}
        isLoading={isLoading}
        htmlAttrs={{ style: { paddingInline: '24px' } }}
        {...testId('button-purchases-banner')}
      >
        {bannerStyles[bannerVariant].buttonTitle}
      </Button>
    )
  }

  function trackTrackOrderButtonClick(orderId: number | null) {
    return beholder.shot('structuredEvent', {
      category: 'my_purchases',
      action: 'view_order_tracking',
      label: 'order_tracking',
      property: 'order_number',
      value: orderId,
    })
  }

  function renderTrackingButton(trackingUrl: string, orderId: string | number) {
    if (!trackingUrl) {
      return
    }
    return (
      <a href={trackingUrl} target="_blank" rel="noreferrer">
        <Button
          ariaLabel={t.trackOrder}
          size="small"
          fontColor={bannerStyles[bannerVariant].fontColorButton}
          color={bannerStyles[bannerVariant].backgroundButton}
          isLoading={isLoading}
          htmlAttrs={{ style: { paddingInline: '24px' } }}
          onClick={() => trackTrackOrderButtonClick(Number(orderId) || null)}
          {...testId('button-track-order-banner')}
        >
          {t.trackOrder}
        </Button>
      </a>
    )
  }

  function renderSuccessMessage(successMessage: string) {
    return (
      <Center>
        <Box marginTop="x20">
          <AlertTag content={successMessage} variant={AlertTagVariants.info} />
        </Box>
      </Center>
    )
  }

  return (
    <BannerContainer
      width="100%"
      display="flex"
      justifyContent="center"
      flexDirection="column"
      variant={bannerVariant}
      hasPaddingBottom={hasPaddingBottom}
      py={['x32', 'x64']}
    >
      <Grid gridTemplateColumns="repeat(12, 1fr)">
        <Grid gridColumn={['2 / 12']}>
          <Title
            textSize={['x3', 'x4']}
            textAlign={'center'}
            color={bannerStyles[bannerVariant].titleFontColor}
            isBrand
            textTransform="uppercase"
            {...testId('purchases-banner-title')}
          >
            {shouldKeepTitle
              ? bannerStyles[bannerVariant].title
              : `${bannerStyles[bannerVariant].title}${orderId}`}
          </Title>
        </Grid>
        <Grid gridColumn={['2 / 12', '4 / 10']}>
          <Center>
            {shouldKeepTitle ? (
              <Text
                textSize={['x14', 'x2']}
                color={bannerStyles[bannerVariant].subtitleFontColor}
              >
                {bannerStyles[bannerVariant].subtitle}
              </Text>
            ) : (
              <>
                <Text
                  textSize={['x14', 'x2']}
                  color={bannerStyles[bannerVariant].subtitleFontColor}
                  {...testId('purchases-banner-info')}
                  htmlAttrs={{
                    'aria-label': `${t.deliveryDate} ${deliveryDate}, ${t.orderDate} ${orderDate}`,
                  }}
                >
                  <b aria-hidden style={{ fontWeight: 'bold' }}>
                    {t.deliveryDate}
                  </b>
                  {deliveryDate} ·{' '}
                  <b aria-hidden style={{ fontWeight: 'bold' }}>
                    {t.orderDate}
                  </b>
                  {orderDate}
                </Text>
                {paymentDue ? (
                  <Text
                    textAlign="center"
                    textSize={['x14', 'x2']}
                    color={bannerStyles[bannerVariant].subtitleFontColor}
                    {...testId('purchases-banner-info')}
                    htmlAttrs={{
                      'aria-label': `${t.paymentDue} `,
                    }}
                  >
                    <b aria-hidden style={{ fontWeight: 'bold' }}>
                      {t.paymentDue}
                    </b>
                    {paymentDue}
                  </Text>
                ) : null}
              </>
            )}
          </Center>
        </Grid>
        <Grid gridColumn={['2 / 12', '4 / 10']}>
          <Center>
            <Box
              paddingTop="x16"
              display="flex"
              flexDirection={['column', 'row']}
              alignItems={['center', 'flex-start']}
              {...testId('purchase-banner-action')}
            >
              {errorMessage ? (
                renderErrorMessage(errorMessage)
              ) : (
                <Box marginRight={['x0', 'x16']} marginBottom={['x16', 'x0']}>
                  {renderBannerAction()}
                </Box>
              )}
              {bannerVariant === PurchasesVariants.Approved &&
                renderTrackingButton(trackingUrl, orderId)}
            </Box>
          </Center>
          {successMessage && renderSuccessMessage(successMessage)}
        </Grid>
      </Grid>
    </BannerContainer>
  )
}
